// Hover-intent Explain affordance (docs/design/plan-2026-07-17-agent-widget-p2.md).
// Wraps a number/record target; after ~600ms hover (or focus) a "✦ Explain" chip
// appears, absolutely positioned so there's zero layout shift. Clicking seeds the
// agent widget in explain mode with the snapshot + prompt (smart model).
// The always-visible variant is `mui/ExplainButton` (design's pulse cards use it);
// this is for targets that shouldn't carry a permanent button.
import { useRef, useState, type ReactNode } from "react";
import { useAgent } from "./AgentProvider";
import "./agent.css";

const HOVER_MS = 600;

export function Explain({
  snapshot,
  prompt,
  children,
}: {
  snapshot: Record<string, unknown>;
  prompt?: string;
  children: ReactNode;
}) {
  const { openAgent, pageContext } = useAgent();
  const [armed, setArmed] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const defaultPrompt = () => {
    const where = pageContext?.entity ? ` for ${pageContext.entity.name ?? pageContext.entity.id}` : "";
    const tab = pageContext?.tab ? ` (${pageContext.tab} tab)` : "";
    const unit = snapshot.unit ? ` ${String(snapshot.unit)}` : "";
    return `Explain this: ${String(snapshot.label)} = ${String(snapshot.value)}${unit}${where}${tab}. Use tools to find out why it is what it is; cite every claim.`;
  };
  const fire = () => openAgent({ kind: "explain", snapshot, prompt: prompt ?? defaultPrompt(), modelHint: "smart" });

  const disarm = () => {
    window.clearTimeout(timer.current);
    setArmed(false);
  };

  return (
    <span
      className="agent-explainable"
      onPointerEnter={() => {
        timer.current = window.setTimeout(() => setArmed(true), HOVER_MS);
      }}
      onPointerLeave={disarm}
      onFocus={() => setArmed(true)}
      onBlur={disarm}
      tabIndex={0}
    >
      {children}
      {armed && (
        <button
          type="button"
          className="agent-explain-chip"
          onClick={fire}
          aria-label={`Explain ${String(snapshot.label ?? "this")}`}
        >
          ✦ Explain
        </button>
      )}
    </span>
  );
}
